import React, { useState, useRef, useEffect, useCallback } from 'react'
import { LANGUAGE_MAP, LANGUAGE_LIST, CODE_LOG_CONFIG } from '../utils/constants'
import { highlight } from '../utils/codeHelpers'
import { detectLanguageFromPaste, getLanguageInfo, truncateFilename } from '../utils/fileHelpers'

/* ───────────────────────────────────────────────────────────────
 *  CODE FILE PAD CONFIG
 *
 *  This is the per-file editor: title + language chip + scrollable
 *  code area + multipurpose menu (Browse/Copy/Star/Delete).
 *  Visual knobs first so you can tune without hunting in JSX.
 * ─────────────────────────────────────────────────────────────── */
const PAD_CONFIG = {
  // Header chrome
  headerPaddingTop: '14px',
  headerPaddingX: '16px',
  headerPaddingBottom: '8px',
  glassCircleSize: 44,                       // ← bigger, matches media/file/chevron buttons app-wide
  glassCircleBg: 'rgba(255,255,255,0.08)',
  glassCircleBorder: '0.5px solid rgba(255,255,255,0.12)',
  glassIconSize: 20,                         // ← icon inside the 44px circle

  // Title
  titleFontSize: '32px',
  titleFontWeight: 700,
  titleLetterSpacing: '-0.5px',
  titleColor: '#fff',
  titlePaddingX: '16px',
  titleMarginTop: '14px',
  titleMarginBottom: '12px',

  // Language chip — PILL shape, NO colored background, AirLog-blue text, NOT bold
  chipFontSize: '14px',                      // ← bigger
  chipFontWeight: 400,                       // ← not bold
  chipLetterSpacing: '0',                    // ← no wide tracking
  chipPaddingY: '7px',
  chipPaddingX: '16px',
  chipRadius: '100px',                       // ← pill
  chipBg: 'rgba(255,255,255,0.08)',          // ← neutral dark pill, same for every language
  chipBorder: '1px solid rgba(121,121,121,0.5)', // ← #797979 stroke @ 50% opacity (stroke only, fill unchanged)
  chipTextColor: '#2f93ff',                  // ← AirLog blue text, always
  chipMarginX: '16px',
  chipMarginBottom: '22px',

  // Code area — Inter font now (not mono)
  codePaddingX: '16px',
  codePaddingTop: '8px',
  codePaddingBottom: '40px',   // no keyboard toolbar anymore
  codeFontSize: '15px',
  codeLineHeight: 1.6,
  codeFontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
  codeFontWeight: 400,
  lineNumberWidth: '36px',
  lineNumberColor: 'rgba(255,255,255,0.26)',
  lineNumberFontSize: '13px',

  // Placeholder
  placeholderColor: 'rgba(255,255,255,0.32)',

  // Language popover — BIGGER, dark-tint liquid glass, roomy rows, dividers inset
  popoverTop: '56px',
  popoverWidth: '230px',                     // ← wider
  popoverItemHeight: '48px',                 // ← taller rows
  popoverItemPaddingX: '18px',
  popoverItemFontSize: '16px',               // ← bigger font
  popoverItemLetterSpacing: '0',
  popoverItemColor: '#2f93ff',               // ← all rows AirLog blue, not per-language color
  popoverItemFontWeight: 400,                // ← not bold
  popoverBg: 'rgba(22, 22, 24, 0.6)',         // ← darker tint, more glass
  popoverBorder: '1px solid rgba(121,121,121,0.5)', // ← #797979 stroke @ 50% opacity (stroke only, fill unchanged)
  popoverRadius: '18px',
  popoverDividerInset: '18px',               // ← divider doesn't touch the edges
  popoverMaxVisibleItems: 7,                 // 7 visible before scroll

  // Action menu (… popover) — bigger to match
  menuWidth: '210px',
  menuItemHeight: '48px',
  menuItemFontSize: '16px',
  menuTop: '56px',
  menuRight: '0px',
  menuDividerInset: '16px',

  // Keyboard toolbar (above iOS keyboard)
  kbBarHeight: '42px',
  kbBarBg: 'rgba(30, 30, 32, 0.94)',
  kbBarItemPadX: '14px',
  kbBarItemFontSize: '16px',
  kbBarItemColor: 'rgba(255,255,255,0.85)',
}

/* ── SF Symbol SVGs ── */
const SFChevronLeft = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 7 11" fill="currentColor"><path d="M0 5.18555C0.00585938 4.98633 0.0761719 4.81641 0.234375 4.66406L4.80469 0.193359C4.92773 0.0644531 5.0918 0 5.2793 0C5.66016 0 5.95898 0.292969 5.95898 0.673828C5.95898 0.861328 5.88281 1.03125 5.75391 1.16602L1.63477 5.18555L5.75391 9.20508C5.88281 9.33398 5.95898 9.50391 5.95898 9.69141C5.95898 10.0781 5.66016 10.3711 5.2793 10.3711C5.0918 10.3711 4.92773 10.3066 4.80469 10.1777L0.234375 5.70703C0.0761719 5.55469 0 5.38477 0 5.18555Z"/></svg>
const SFPencilSquare = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 12 12" fill="currentColor"><path d="M11.127 1.54102L10.2305 0.650391L10.6465 0.240234C10.8574 0.0292969 11.1914 0 11.3906 0.193359L11.543 0.351562C11.7656 0.568359 11.7598 0.890625 11.5312 1.125L11.127 1.54102ZM4.13672 7.83984C4.00195 7.89258 3.85547 7.74023 3.91406 7.60547L4.44141 6.45117L9.78516 1.10156L10.6758 1.98633L5.33203 7.33594L4.13672 7.83984ZM2.51367 11.748C0.902344 11.748 0 10.8516 0 9.23438V3.73828C0 2.12695 0.902344 1.22461 2.51367 1.22461L7.97461 1.23047C8.12695 1.23047 8.27344 1.24219 8.4082 1.26562L7.2832 2.39648C7.16016 2.37891 7.02539 2.37305 6.89062 2.37305H2.54297C1.65234 2.37305 1.14844 2.87695 1.14844 3.76758V9.20508C1.14844 10.1016 1.65234 10.6055 2.54297 10.6055H7.98633C8.87695 10.6055 9.38086 10.1016 9.38086 9.20508V4.78125C9.38086 4.68164 9.375 4.59375 9.35742 4.5L10.4883 3.375C10.5117 3.50977 10.5293 3.65625 10.5293 3.80859V9.23438C10.5293 10.8516 9.62695 11.748 8.01562 11.748H2.51367Z"/></svg>
const SFEllipsis = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 17 4" fill="currentColor"><circle cx="2" cy="2" r="2"/><circle cx="8.5" cy="2" r="2"/><circle cx="15" cy="2" r="2"/></svg>
const SFBrowse = ({ size = 20 }) => <svg width={size} height={size * 11 / 14} viewBox="0 0 14 11" fill="currentColor"><path d="M1.92188 10.916C0.65625 10.916 0 10.2656 0 9.01172V1.86914C0 0.638672 0.644531 0 1.74023 0H3.39844C4.00781 0 4.3125 0.111328 4.72266 0.451172L5.0625 0.720703C5.38477 0.984375 5.63086 1.08398 6.05273 1.08398H11.502C12.7676 1.08398 13.4238 1.73438 13.4238 2.98828V9.01172C13.4238 10.2656 12.7734 10.916 11.6543 10.916H1.92188ZM1.99219 9.76758H11.4375C11.9766 9.76758 12.2754 9.48633 12.2754 8.91797V3.08789C12.2754 2.51367 11.9766 2.23242 11.4375 2.23242H5.74805C5.13867 2.23242 4.82227 2.12109 4.41797 1.78711L4.07812 1.51172C3.75 1.24219 3.50977 1.14844 3.09375 1.14844H1.96875C1.44141 1.14844 1.14844 1.42969 1.14844 1.96875V8.91797C1.14844 9.48633 1.44727 9.76758 1.99219 9.76758ZM1.86328 3.9082V3.49219C1.86328 3.12305 2.05078 2.95312 2.42578 2.95312H10.998C11.373 2.95312 11.5547 3.12305 11.5547 3.49219V3.9082H1.86328Z"/></svg>
const SFCopy = ({ size = 20 }) => <svg width={size * 13 / 15} height={size} viewBox="0 0 13 15" fill="currentColor"><path d="M0 12.9785V4.92188C0 3.66211 0.650391 3 1.9043 3H2.89453V1.92188C2.89453 0.65625 3.55078 0 4.79883 0H7.00781C7.68164 0 8.20312 0.169922 8.64258 0.621094L11.5664 3.59766C12.0293 4.07227 12.1875 4.56445 12.1875 5.33203V9.97266C12.1875 11.2383 11.5371 11.8945 10.2832 11.8945H9.28711V12.9785C9.28711 14.2383 8.64258 14.9004 7.38867 14.9004H1.9043C0.650391 14.9004 0 14.2441 0 12.9785ZM8.70117 6.82031C9.19336 7.31836 9.28711 7.68164 9.28711 8.47266V10.7461H10.1836C10.7578 10.7461 11.0391 10.4473 11.0391 9.90234V5.05078H8.45508C7.73438 5.05078 7.34766 4.67578 7.34766 3.94922V1.14844H4.89844C4.33008 1.14844 4.04297 1.44727 4.04297 1.98633V3.00586C4.69336 3.01172 5.07422 3.13477 5.52539 3.5918L8.70117 6.82031ZM8.30859 3.80273C8.30859 4.00781 8.39648 4.08984 8.5957 4.08984H10.7402L8.30859 1.62305V3.80273ZM1.14844 12.9082C1.14844 13.4531 1.42969 13.752 1.99805 13.752H7.28906C7.85742 13.752 8.13867 13.4531 8.13867 12.9082V8.60742H5.04492C4.21289 8.60742 3.79688 8.19727 3.79688 7.35938V4.14844H1.99805C1.42969 4.14844 1.14844 4.45312 1.14844 4.99219V12.9082ZM5.15625 7.59375H7.98047L4.81055 4.37109V7.24805C4.81055 7.48828 4.91602 7.59375 5.15625 7.59375Z"/></svg>
const SFStar = ({ size = 20 }) => <svg width={size} height={size * 13 / 14} viewBox="0 0 14 13" fill="currentColor"><path d="M2.60742 12.4863C2.33203 12.2812 2.27344 11.9414 2.43164 11.4785L3.63867 7.89844L0.5625 5.68945C0.164062 5.4082 0 5.09766 0.111328 4.76953C0.222656 4.44727 0.527344 4.28906 1.01367 4.29492L4.78711 4.31836L5.93555 0.720703C6.08789 0.251953 6.32812 0 6.66797 0C7.01367 0 7.25391 0.251953 7.40039 0.720703L8.54883 4.31836L12.3223 4.29492C12.8086 4.28906 13.1191 4.44727 13.2305 4.76953C13.3418 5.09766 13.1719 5.4082 12.7793 5.68945L9.70312 7.89844L10.9043 11.4785C11.0625 11.9414 11.0039 12.2812 10.7344 12.4863C10.459 12.6973 10.1133 12.627 9.71484 12.3398L6.66797 10.1016L3.62109 12.3398C3.22266 12.627 2.88281 12.6973 2.60742 12.4863ZM3.63281 11.0742C3.64453 11.0918 3.66211 11.0801 3.68555 11.0684L6.32812 9.0293C6.5625 8.8418 6.77344 8.8418 7.01367 9.0293L9.65625 11.0684C9.67969 11.0801 9.69141 11.0918 9.70312 11.0742C9.71484 11.0625 9.71484 11.0566 9.70312 11.0273L8.58984 7.88086C8.49023 7.59375 8.52539 7.41797 8.79492 7.23633L11.543 5.34961C11.5723 5.33203 11.5781 5.32617 11.5723 5.30859C11.5664 5.29688 11.5547 5.29102 11.5195 5.29102L8.18555 5.36719C7.88672 5.37891 7.72852 5.2793 7.64062 4.98633L6.70312 1.78711C6.69141 1.75195 6.68555 1.74609 6.66797 1.74609C6.65625 1.74609 6.64453 1.75195 6.63867 1.78711L5.69531 4.98633C5.60742 5.2793 5.45508 5.37891 5.15039 5.36719L1.81641 5.29102C1.78711 5.29102 1.77539 5.29688 1.76367 5.30859C1.75781 5.32617 1.76953 5.33789 1.79297 5.34961L4.54688 7.23633C4.81055 7.41211 4.8457 7.59375 4.74609 7.88086L3.63281 11.0273C3.62695 11.0566 3.62695 11.0625 3.63281 11.0742Z"/></svg>
const SFStarFill = ({ size = 20 }) => <svg width={size} height={size * 13 / 14} viewBox="0 0 14 13" fill="currentColor"><path d="M2.60742 12.4863C2.33203 12.2812 2.27344 11.9414 2.43164 11.4785L3.63867 7.89844L0.5625 5.68945C0.164062 5.4082 0 5.09766 0.111328 4.76953C0.222656 4.44727 0.527344 4.29492 1.01367 4.29492H4.78711L5.93555 0.720703C6.08789 0.251953 6.32812 0 6.66797 0C7.01367 0 7.25391 0.251953 7.40039 0.720703L8.54883 4.29492H12.3223C12.8086 4.29492 13.1191 4.44727 13.2305 4.76953C13.3418 5.09766 13.1719 5.4082 12.7793 5.68945L9.70312 7.89844L10.9043 11.4785C11.0625 11.9414 11.0039 12.2812 10.7344 12.4863C10.459 12.6973 10.1133 12.627 9.71484 12.3398L6.66797 10.1016L3.62109 12.3398C3.22266 12.627 2.88281 12.6973 2.60742 12.4863Z"/></svg>
const SFTrash = ({ size = 20 }) => <svg width={size * 12 / 14} height={size} viewBox="0 0 12 14" fill="currentColor"><path d="M3.08789 13.2246C2.17969 13.2246 1.60547 12.6855 1.56445 11.7832L1.16602 3.33398H0.515625C0.234375 3.33398 0 3.11133 0 2.81836C0 2.53125 0.234375 2.30859 0.515625 2.30859H3.11719V1.42969C3.11719 0.533203 3.69141 0 4.6582 0H6.88477C7.85742 0 8.42578 0.533203 8.42578 1.42969V2.30859H11.0332C11.3262 2.30859 11.5547 2.53125 11.5547 2.81836C11.5547 3.11133 11.3262 3.33398 11.0332 3.33398H10.3887L9.99023 11.7773C9.94922 12.6797 9.36914 13.2246 8.47266 13.2246H3.08789ZM4.19531 1.48242V2.30859H7.34766V1.48242C7.34766 1.17773 7.13672 0.984375 6.80859 0.984375H4.73438C4.40625 0.984375 4.19531 1.17773 4.19531 1.48242ZM3.19922 12.1875H8.34375C8.67773 12.1875 8.91211 11.9531 8.92383 11.6074L9.30469 3.33398H2.22656L2.625 11.6074C2.64258 11.9531 2.87695 12.1875 3.19922 12.1875ZM4.04883 11.2441C3.81445 11.2441 3.65039 11.0918 3.64453 10.8574L3.46875 4.7168C3.46289 4.48828 3.62109 4.33008 3.86719 4.33008C4.10156 4.33008 4.26562 4.48242 4.27148 4.7168L4.44727 10.8574C4.45312 11.0859 4.29492 11.2441 4.04883 11.2441ZM5.77734 11.2441C5.53125 11.2441 5.36719 11.0859 5.36719 10.8574V4.7168C5.36719 4.48828 5.53125 4.33008 5.77734 4.33008C6.02344 4.33008 6.1875 4.48828 6.1875 4.7168V10.8574C6.1875 11.0859 6.02344 11.2441 5.77734 11.2441ZM7.50586 11.2441C7.25977 11.2441 7.0957 11.0859 7.10156 10.8574L7.2832 4.7168C7.28906 4.48242 7.45312 4.33008 7.6875 4.33008C7.92773 4.33008 8.0918 4.48828 8.08594 4.7168L7.9043 10.8574C7.89844 11.0918 7.74023 11.2441 7.50586 11.2441Z"/></svg>

const glassCircleStyle = {
  width: PAD_CONFIG.glassCircleSize + 'px',
  height: PAD_CONFIG.glassCircleSize + 'px',
  borderRadius: '50%',
  background: PAD_CONFIG.glassCircleBg,
  border: PAD_CONFIG.glassCircleBorder,
  backdropFilter: 'saturate(180%) blur(14px)',
  WebkitBackdropFilter: 'saturate(180%) blur(14px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
}

/* ───────────────────────────────────────────────────────────────
 *  Detect mobile so we know whether to wrap (phone) or
 *  horizontal-scroll (desktop/iPad). Cached on mount.
 * ─────────────────────────────────────────────────────────────── */
const isPhoneViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches

/* ───────────────────────────────────────────────────────────────
 *  CodeFilePad — full-screen note pad for a single code file.
 *
 *  Props:
 *    file          : { name, language, content }
 *    onChange      : (updatedFile) => void
 *    onBack        : () => void
 *    onCopy        : () => void    (multipurpose menu)
 *    onBrowse      : () => void    (multipurpose menu — opens file picker)
 *    onToggleStar  : () => void    (stars the whole log)
 *    isStarred     : boolean       (visual state of star menu item)
 *    onDeleteFile  : () => void    (deletes THIS file from the folder)
 *    fileIndex     : number        (for placeholder fallback "File 1")
 *    inBackground  : boolean       (pad behind sheet → only chevron works)
 * ─────────────────────────────────────────────────────────────── */
function CodeFilePad({ file, onChange, onBack, onAddFile, onCopy, onBrowse, onToggleStar, isStarred, onDeleteFile, fileIndex, inBackground }) {
  const [showLangPopover, setShowLangPopover] = useState(false)
  const [showActionMenu, setShowActionMenu] = useState(false)
  const [isPhone] = useState(isPhoneViewport)
  const textareaRef = useRef(null)
  const titleRef = useRef(null)

  const langInfo = getLanguageInfo(file.language)

  /* Mirror textarea content as highlighted HTML behind it. */
  const highlightedHtml = highlight(file.content || '', file.language)

  /* Real line count for the gutter — always matches the content, because
   * the gutter now lives INSIDE the scrolling overlay (see below), it
   * scrolls 1:1 with the code and can never "stop early". */
  const lineCount = Math.max(1, (file.content || '').split('\n').length)

  /* When typing, keep the highlight overlay scroll synced to textarea. */
  const overlayRef = useRef(null)
  const handleScrollSync = useCallback(() => {
    if (overlayRef.current && textareaRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop
      overlayRef.current.scrollLeft = textareaRef.current.scrollLeft
    }
  }, [])

  /* ── Paste handler: try to auto-detect language if none set ── */
  const handlePaste = (e) => {
    if (file.language) return  // user already picked, don't override
    const pasted = e.clipboardData?.getData('text') || ''
    const detected = detectLanguageFromPaste({ filename: file.name, content: pasted })
    if (detected) {
      // Slight delay to let the paste land first
      setTimeout(() => onChange({ ...file, language: detected }), 0)
    }
  }

  /* ── Close popovers when clicking elsewhere ── */
  useEffect(() => {
    if (!showLangPopover && !showActionMenu) return
    const handler = (e) => {
      if (e.target.closest('[data-popover-root]')) return
      setShowLangPopover(false)
      setShowActionMenu(false)
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('touchstart', handler, { passive: true })
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('touchstart', handler)
    }
  }, [showLangPopover, showActionMenu])

  const titleValue = file.name || `${CODE_LOG_CONFIG.defaultFileNamePrefix} ${fileIndex + 1}`
  const placeholderText = 'Code here…'

  /* Shared metrics so gutter + code + textarea all use the same line box. */
  const codeTextStyle = {
    fontFamily: PAD_CONFIG.codeFontFamily,
    fontSize: PAD_CONFIG.codeFontSize,
    fontWeight: PAD_CONFIG.codeFontWeight,
    lineHeight: PAD_CONFIG.codeLineHeight,
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100000,
      background: '#0a0a0b',
      display: 'flex', flexDirection: 'column',
      color: '#fff',
    }}>
      {/* HEADER */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `${PAD_CONFIG.headerPaddingTop} ${PAD_CONFIG.headerPaddingX} ${PAD_CONFIG.headerPaddingBottom}`,
        flexShrink: 0,
      }}>
        <button onClick={onBack} style={glassCircleStyle}><SFChevronLeft size={PAD_CONFIG.glassIconSize} /></button>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative', opacity: inBackground ? 0 : 1, pointerEvents: inBackground ? 'none' : 'auto' }} data-popover-root>
          <button
            onClick={() => { if (inBackground) return; onAddFile && onAddFile() }}
            style={glassCircleStyle}
            title="Add file"
            disabled={inBackground}
          >
            <SFPencilSquare size={PAD_CONFIG.glassIconSize} />
          </button>
          <button
            onClick={() => { if (inBackground) return; setShowActionMenu(s => !s); setShowLangPopover(false) }}
            style={glassCircleStyle}
            title="More"
            disabled={inBackground}
          >
            <SFEllipsis size={PAD_CONFIG.glassIconSize} />
          </button>

          {/* Action menu */}
          {showActionMenu && (
            <div style={{
              position: 'absolute',
              top: PAD_CONFIG.menuTop,
              right: 0,
              width: PAD_CONFIG.menuWidth,
              background: PAD_CONFIG.popoverBg,
              border: PAD_CONFIG.popoverBorder,
              borderRadius: PAD_CONFIG.popoverRadius,
              backdropFilter: 'blur(30px) saturate(180%)',
              WebkitBackdropFilter: 'blur(30px) saturate(180%)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.55)',
              overflow: 'hidden',
              zIndex: 5,
            }}>
              {[
                { label: 'Browse', icon: <SFBrowse />, onClick: () => { setShowActionMenu(false); onBrowse() } },
                { label: 'Copy', icon: <SFCopy />, onClick: () => { setShowActionMenu(false); onCopy() } },
                { label: isStarred ? 'Unstar' : 'Star', icon: isStarred ? <SFStarFill /> : <SFStar />, onClick: () => { setShowActionMenu(false); onToggleStar() } },
                { label: 'Delete', icon: <SFTrash />, onClick: () => { setShowActionMenu(false); onDeleteFile() }, danger: true },
              ].map((item, i) => (
                <button key={i} onClick={item.onClick} style={{
                  width: '100%',
                  height: PAD_CONFIG.menuItemHeight,
                  background: 'none',
                  border: 'none',
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: `0 ${PAD_CONFIG.popoverItemPaddingX}`,
                  color: item.danger ? '#FF453A' : '#fff',
                  fontSize: PAD_CONFIG.menuItemFontSize,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  position: 'relative',
                }}>
                  {/* inset divider on top of every row except the first */}
                  {i !== 0 && (
                    <span style={{
                      position: 'absolute', top: 0,
                      left: PAD_CONFIG.menuDividerInset, right: 0,
                      height: '0.5px', background: 'rgba(255,255,255,0.1)',
                    }} />
                  )}
                  <span style={{ display: 'flex', width: '22px', justifyContent: 'center', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* TITLE (editable) */}
      <input
        ref={titleRef}
        type="text"
        value={titleValue}
        onChange={(e) => onChange({ ...file, name: e.target.value })}
        readOnly={inBackground}
        placeholder={`${CODE_LOG_CONFIG.defaultFileNamePrefix} ${fileIndex + 1}`}
        style={{
          fontSize: PAD_CONFIG.titleFontSize,
          fontWeight: PAD_CONFIG.titleFontWeight,
          letterSpacing: PAD_CONFIG.titleLetterSpacing,
          color: PAD_CONFIG.titleColor,
          background: 'none',
          border: 'none',
          outline: 'none',
          padding: `0 ${PAD_CONFIG.titlePaddingX}`,
          marginTop: PAD_CONFIG.titleMarginTop,
          marginBottom: PAD_CONFIG.titleMarginBottom,
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          flexShrink: 0,
        }}
      />

      {/* LANGUAGE CHIP */}
      <div style={{ position: 'relative', padding: `0 ${PAD_CONFIG.chipMarginX}`, marginBottom: PAD_CONFIG.chipMarginBottom, flexShrink: 0, opacity: inBackground ? 0 : 1, pointerEvents: inBackground ? 'none' : 'auto' }} data-popover-root>
        <button
          onClick={() => { if (inBackground) return; setShowLangPopover(s => !s); setShowActionMenu(false) }}
          style={{
            background: PAD_CONFIG.chipBg,
            color: PAD_CONFIG.chipTextColor,
            border: PAD_CONFIG.chipBorder,          // ← 50%-opacity stroke (fill unchanged)
            borderRadius: PAD_CONFIG.chipRadius,
            padding: `${PAD_CONFIG.chipPaddingY} ${PAD_CONFIG.chipPaddingX}`,
            fontSize: PAD_CONFIG.chipFontSize,
            fontWeight: PAD_CONFIG.chipFontWeight,
            letterSpacing: PAD_CONFIG.chipLetterSpacing,
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
          }}
        >
          {langInfo ? langInfo.label : 'Languages'}
        </button>

        {/* Language popover */}
        {showLangPopover && (
          <div style={{
            position: 'absolute',
            top: PAD_CONFIG.popoverTop,
            left: PAD_CONFIG.chipMarginX,
            width: PAD_CONFIG.popoverWidth,
            maxHeight: `calc(${PAD_CONFIG.popoverMaxVisibleItems} * ${PAD_CONFIG.popoverItemHeight})`,
            overflowY: 'auto',
            background: PAD_CONFIG.popoverBg,
            border: PAD_CONFIG.popoverBorder,
            borderRadius: PAD_CONFIG.popoverRadius,
            backdropFilter: 'blur(30px) saturate(180%)',
            WebkitBackdropFilter: 'blur(30px) saturate(180%)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.55)',
            zIndex: 5,
          }} className="no-sb">
            {LANGUAGE_LIST.map((id, i) => {
              const info = LANGUAGE_MAP[id]
              return (
                <button
                  key={id}
                  onClick={() => { onChange({ ...file, language: id }); setShowLangPopover(false) }}
                  style={{
                    width: '100%',
                    height: PAD_CONFIG.popoverItemHeight,
                    background: 'none',
                    border: 'none',
                    color: PAD_CONFIG.popoverItemColor,
                    fontSize: PAD_CONFIG.popoverItemFontSize,
                    fontWeight: PAD_CONFIG.popoverItemFontWeight,
                    letterSpacing: PAD_CONFIG.popoverItemLetterSpacing,
                    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                    textAlign: 'left',
                    padding: `0 ${PAD_CONFIG.popoverItemPaddingX}`,
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {/* inset divider above each row except the first */}
                  {i !== 0 && (
                    <span style={{
                      position: 'absolute', top: 0,
                      left: PAD_CONFIG.popoverDividerInset, right: PAD_CONFIG.popoverDividerInset,
                      height: '0.5px', background: 'rgba(255,255,255,0.08)',
                    }} />
                  )}
                  {info.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* CODE AREA — textarea + highlight overlay (line gutter INSIDE the overlay) */}
      <div style={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Highlight overlay (read-only, BEHIND the textarea).
            It's a flex row: [ line-number gutter | highlighted code ].
            Because the gutter is INSIDE this same scrolling element, the
            numbers scroll exactly with the code and always match the real
            line count — no more "numbers stop at 33 while code has 345". */}
        <div
          ref={overlayRef}
          aria-hidden="true"
          className="codepad-overlay no-sb"
          style={{
            position: 'absolute',
            inset: 0,
            margin: 0,
            paddingTop: PAD_CONFIG.codePaddingTop,
            paddingBottom: PAD_CONFIG.codePaddingBottom,
            overflow: 'auto',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'flex-start',
            background: 'transparent',
          }}
        >
          {/* Line-number gutter — same lineHeight as the code, so row N
              aligns with code row N. Sticky-left so it stays put when the
              code scrolls sideways. */}
          <div style={{
            flexShrink: 0,
            position: 'sticky',
            left: 0,
            width: PAD_CONFIG.lineNumberWidth,
            paddingRight: '8px',
            boxSizing: 'border-box',
            textAlign: 'right',
            color: PAD_CONFIG.lineNumberColor,
            fontFamily: PAD_CONFIG.codeFontFamily,
            fontSize: PAD_CONFIG.codeFontSize,            // match code font-size for exact row height
            lineHeight: PAD_CONFIG.codeLineHeight,
            userSelect: 'none',
            background: '#0a0a0b',
            zIndex: 1,
          }}>
            {/* Each number row is forced to EXACTLY the code row's height
                (codeFontSize × codeLineHeight). Without this, the smaller
                number font makes shorter rows and the code drifts ahead. */}
            {Array.from({ length: lineCount }, (_, i) => (
              <div key={i} style={{
                fontSize: PAD_CONFIG.lineNumberFontSize,
                height: `calc(${PAD_CONFIG.codeFontSize} * ${PAD_CONFIG.codeLineHeight})`,
                lineHeight: `calc(${PAD_CONFIG.codeFontSize} * ${PAD_CONFIG.codeLineHeight})`,
                whiteSpace: 'nowrap',
              }}>{i + 1}</div>
            ))}
          </div>

          {/* Highlighted code — NO WRAP: every line is exactly one row, so
              the gutter numbers always line up 1:1 (long lines scroll
              sideways, like a real code editor). */}
          <pre
            dangerouslySetInnerHTML={{ __html: highlightedHtml + '\n' }}
            style={{
              margin: 0,
              padding: 0,
              paddingRight: PAD_CONFIG.codePaddingX,
              flex: 1,
              minWidth: 0,
              ...codeTextStyle,
              color: 'rgba(229,229,234,1)',
              whiteSpace: 'pre',
              wordBreak: 'normal',
            }}
          />
        </div>

        {/* Real textarea (transparent text, only the overlay shows colors).
            Left padding equals the gutter width so the caret + typed text
            line up with the highlighted code behind it. */}
        <textarea
          ref={textareaRef}
          value={file.content || ''}
          onChange={(e) => onChange({ ...file, content: e.target.value })}
          onScroll={handleScrollSync}
          onPaste={handlePaste}
          readOnly={inBackground}
          placeholder={placeholderText}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            padding: `${PAD_CONFIG.codePaddingTop} ${PAD_CONFIG.codePaddingX} ${PAD_CONFIG.codePaddingBottom} ${PAD_CONFIG.lineNumberWidth}`,
            background: 'transparent',
            color: 'transparent',
            caretColor: '#fff',
            border: 'none',
            outline: 'none',
            resize: 'none',
            ...codeTextStyle,
            whiteSpace: 'pre',
            wordBreak: 'normal',
            overflowWrap: 'normal',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            zIndex: 1,
          }}
          className="codepad-textarea no-sb"
        />

        {/* Style block: placeholder color and selection match */}
        <style>{`
          .codepad-textarea::placeholder { color: ${PAD_CONFIG.placeholderColor}; }
          .codepad-textarea::selection { background: rgba(0, 122, 255, 0.35); }
          .codepad-overlay { background: transparent; }
        `}</style>
      </div>
    </div>
  )
}

export default CodeFilePad